import React from 'react';
import { Typography, Card, Divider, Space, Button } from 'antd';
import { CalendarOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;

function TermsOfService() {
    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <Title level={1} className="!text-white !mb-4">
                        Terms of Service
                    </Title>
                    <Paragraph className="!text-green-100 !text-lg">
                        Please read these terms carefully before using our catering platform services.
                    </Paragraph>
                    <Space className="mt-4">
                        <Text className="text-green-100">
                            <CalendarOutlined className="mr-1" />
                            Last updated: January 27, 2025
                        </Text>
                    </Space>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <Card>
                    <div className="prose prose-lg max-w-none">
                        {/* Introduction */}
                        <section className="mb-8">
                            <Title level={2}>Agreement to Terms</Title>
                            <Paragraph>
                                These Terms of Service ("Terms") constitute a legally binding agreement between you ("User," "Customer," or "you") and Spoon & Spice Private Limited ("Company," "we," "our," or "us") regarding your use of our catering platform services, website, and mobile application (collectively, the "Services").
                            </Paragraph>
                            <Paragraph>
                                By accessing or using our Services, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Services.
                            </Paragraph>
                        </section>

                        <Divider />

                        {/* Service Description */}
                        <section className="mb-8">
                            <Title level={2}>Description of Services</Title>
                            <Paragraph>
                                Spoon & Spice is a technology platform that connects customers with verified catering vendors for various occasions including weddings, corporate events, parties, and other special events. Our Services include:
                            </Paragraph>
                            <ul>
                                <li>Online platform for browsing and selecting catering vendors</li>
                                <li>Real-time pricing and availability information</li>
                                <li>Booking and payment processing services</li>
                                <li>Customer support and order management</li>
                                <li>Quality assurance and vendor verification</li>
                            </ul>
                            <Paragraph>
                                We act as an intermediary between customers and vendors. The actual catering services are provided by independent third-party vendors who are not employees or agents of Spoon & Spice.
                            </Paragraph>
                        </section>

                        <Divider />

                        {/* User Accounts */}
                        <section className="mb-8">
                            <Title level={2}>User Accounts and Registration</Title>
                            
                            <Title level={3}>Account Creation</Title>
                            <Paragraph>
                                To use certain features of our Services, you must create an account. You agree to:
                            </Paragraph>
                            <ul>
                                <li>Provide accurate, current, and complete information during registration</li>
                                <li>Maintain and update your account information to keep it accurate</li>
                                <li>Maintain the security of your account credentials</li>
                                <li>Accept responsibility for all activities under your account</li>
                                <li>Notify us immediately of any unauthorized use of your account</li>
                            </ul>

                            <Title level={3}>Account Termination</Title>
                            <Paragraph>
                                We reserve the right to suspend or terminate your account at any time for violation of these Terms or for any other reason at our sole discretion.
                            </Paragraph>
                        </section>

                        <Divider />

                        {/* Booking and Orders */}
                        <section className="mb-8">
                            <Title level={2}>Booking and Order Terms</Title>
                            
                            <Title level={3}>Order Process</Title>
                            <Paragraph>
                                When you place an order through our platform:
                            </Paragraph>
                            <ul>
                                <li>You are entering into a contract with the selected vendor, not with Spoon & Spice</li>
                                <li>All orders are subject to vendor availability and acceptance</li>
                                <li>Pricing is determined by the vendor and may vary based on location, date, and services</li>
                                <li>We facilitate the transaction but are not a party to the catering contract</li>
                            </ul>

                            <Title level={3}>Payment Terms</Title>
                            <Paragraph>
                                Payment terms vary by vendor and may include:
                            </Paragraph>
                            <ul>
                                <li>Full payment upfront</li>
                                <li>Partial payment with balance due on delivery</li>
                                <li>Payment plans for large events</li>
                            </ul>
                            <Paragraph>
                                All payments are processed securely through our payment partners. We do not store your payment information.
                            </Paragraph>

                            <Title level={3}>Cancellation and Refunds</Title>
                            <Paragraph>
                                Cancellation policies vary by vendor and are clearly stated during the booking process. Generally:
                            </Paragraph>
                            <ul>
                                <li>Cancellations made 48+ hours in advance: Full refund</li>
                                <li>Cancellations made 24-48 hours in advance: 50% refund</li>
                                <li>Cancellations made less than 24 hours in advance: No refund</li>
                            </ul>
                            <Paragraph>
                                Refunds are processed within 5-7 business days to your original payment method.
                            </Paragraph>
                        </section>

                        <Divider />

                        {/* Vendor Terms */}
                        <section className="mb-8">
                            <Title level={2}>Vendor Terms and Responsibilities</Title>
                            <Paragraph>
                                Vendors on our platform must:
                            </Paragraph>
                            <ul>
                                <li>Maintain all necessary licenses and permits</li>
                                <li>Comply with food safety regulations and standards</li>
                                <li>Provide accurate information about their services and pricing</li>
                                <li>Honor confirmed bookings and deliver services as promised</li>
                                <li>Maintain appropriate insurance coverage</li>
                                <li>Respond promptly to customer inquiries and issues</li>
                            </ul>
                            <Paragraph>
                                We reserve the right to remove vendors from our platform for violations of these terms or for poor service quality.
                            </Paragraph>
                        </section>

                        <Divider />

                        {/* User Conduct */}
                        <section className="mb-8">
                            <Title level={2}>User Conduct and Prohibited Activities</Title>
                            <Paragraph>
                                You agree not to use our Services for any unlawful or prohibited activities, including:
                            </Paragraph>
                            <ul>
                                <li>Violating any applicable laws or regulations</li>
                                <li>Infringing on intellectual property rights</li>
                                <li>Transmitting harmful or malicious code</li>
                                <li>Attempting to gain unauthorized access to our systems</li>
                                <li>Interfering with the proper functioning of our Services</li>
                                <li>Creating fake accounts or providing false information</li>
                                <li>Harassing or abusing other users or vendors</li>
                                <li>Using our Services for commercial purposes without authorization</li>
                            </ul>
                        </section>

                        <Divider />

                        {/* Intellectual Property */}
                        <section className="mb-8">
                            <Title level={2}>Intellectual Property Rights</Title>
                            <Paragraph>
                                The Services and their original content, features, and functionality are owned by Spoon & Spice and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
                            </Paragraph>
                            <Paragraph>
                                You may not:
                            </Paragraph>
                            <ul>
                                <li>Copy, modify, or distribute our content without permission</li>
                                <li>Use our trademarks or logos without authorization</li>
                                <li>Reverse engineer or attempt to extract source code</li>
                                <li>Create derivative works based on our Services</li>
                            </ul>
                        </section>

                        <Divider />

                        {/* Disclaimers */}
                        <section className="mb-8">
                            <Title level={2}>Disclaimers and Limitations</Title>
                            
                            <Title level={3}>Service Availability</Title>
                            <Paragraph>
                                We strive to maintain continuous service availability but cannot guarantee uninterrupted access. Our Services are provided "as is" without warranties of any kind.
                            </Paragraph>

                            <Title level={3}>Third-Party Services</Title>
                            <Paragraph>
                                We are not responsible for the quality, safety, or legality of services provided by vendors. We do not endorse or guarantee any vendor's services.
                            </Paragraph>

                            <Title level={3}>Limitation of Liability</Title>
                            <Paragraph>
                                To the maximum extent permitted by law, Spoon & Spice shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or use, arising from your use of our Services.
                            </Paragraph>
                        </section>

                        <Divider />

                        {/* Indemnification */}
                        <section className="mb-8">
                            <Title level={2}>Indemnification</Title>
                            <Paragraph>
                                You agree to indemnify and hold harmless Spoon & Spice, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorney's fees) arising from:
                            </Paragraph>
                            <ul>
                                <li>Your use of our Services</li>
                                <li>Your violation of these Terms</li>
                                <li>Your violation of any third-party rights</li>
                                <li>Any content you submit or transmit through our Services</li>
                            </ul>
                        </section>

                        <Divider />

                        {/* Dispute Resolution */}
                        <section className="mb-8">
                            <Title level={2}>Dispute Resolution</Title>
                            
                            <Title level={3}>Governing Law</Title>
                            <Paragraph>
                                These Terms shall be governed by and construed in accordance with the laws of India, without regard to conflict of law principles.
                            </Paragraph>

                            <Title level={3}>Dispute Resolution Process</Title>
                            <Paragraph>
                                Any disputes arising from these Terms or your use of our Services shall be resolved through:
                            </Paragraph>
                            <ol>
                                <li>Good faith negotiations between the parties</li>
                                <li>Mediation if negotiations fail</li>
                                <li>Binding arbitration if mediation fails</li>
                            </ol>
                            <Paragraph>
                                Arbitration shall be conducted in Mumbai, India, under the rules of the Indian Arbitration and Conciliation Act.
                            </Paragraph>
                        </section>

                        <Divider />

                        {/* Privacy */}
                        <section className="mb-8">
                            <Title level={2}>Privacy and Data Protection</Title>
                            <Paragraph>
                                Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference.
                            </Paragraph>
                            <Paragraph>
                                By using our Services, you consent to the collection and use of your information as described in our Privacy Policy.
                            </Paragraph>
                        </section>

                        <Divider />

                        {/* Modifications */}
                        <section className="mb-8">
                            <Title level={2}>Modifications to Terms</Title>
                            <Paragraph>
                                We reserve the right to modify these Terms at any time. We will notify users of material changes by:
                            </Paragraph>
                            <ul>
                                <li>Posting the updated Terms on our website</li>
                                <li>Sending email notifications to registered users</li>
                                <li>Displaying prominent notices on our platform</li>
                            </ul>
                            <Paragraph>
                                Your continued use of our Services after the effective date of any changes constitutes acceptance of the modified Terms.
                            </Paragraph>
                        </section>

                        <Divider />

                        {/* Severability */}
                        <section className="mb-8">
                            <Title level={2}>Severability</Title>
                            <Paragraph>
                                If any provision of these Terms is found to be unenforceable or invalid, the remaining provisions will remain in full force and effect. The unenforceable provision will be replaced with a valid provision that most closely approximates the intent of the original provision.
                            </Paragraph>
                        </section>

                        <Divider />

                        {/* Entire Agreement */}
                        <section className="mb-8">
                            <Title level={2}>Entire Agreement</Title>
                            <Paragraph>
                                These Terms, together with our Privacy Policy and any other legal notices published on our Services, constitute the entire agreement between you and Spoon & Spice regarding your use of our Services.
                            </Paragraph>
                        </section>

                        <Divider />

                        {/* Contact Information */}
                        <section className="mb-8">
                            <Title level={2}>Contact Information</Title>
                            <Paragraph>
                                If you have any questions about these Terms of Service, please contact us:
                            </Paragraph>
                            
                            <Card className="bg-gray-50">
                                <Space direction="vertical" size="large" className="w-full">
                                    <div>
                                        <Text strong className="block mb-2">Email:</Text>
                                        <Space>
                                            <MailOutlined />
                                            <a href="mailto:legal@spoonandspice.com">legal@spoonandspice.com</a>
                                        </Space>
                                    </div>
                                    
                                    <div>
                                        <Text strong className="block mb-2">Phone:</Text>
                                        <Space>
                                            <PhoneOutlined />
                                            <a href="tel:+917483423925">+91 7483423925</a>
                                        </Space>
                                    </div>
                                    
                                    <div>
                                        <Text strong className="block mb-2">Address:</Text>
                                        <Text>
                                            Spoon & Spice Private Limited<br />
                                            123 Business District<br />
                                            Mumbai, Maharashtra 400001<br />
                                            India
                                        </Text>
                                    </div>
                                </Space>
                            </Card>
                        </section>

                        {/* Footer */}
                        <div className="text-center mt-12 pt-8 border-t">
                            <Paragraph className="!text-gray-600">
                                These Terms of Service are effective as of January 27, 2025, and were last updated on January 27, 2025.
                            </Paragraph>
                            <Space className="mt-4">
                                <Button type="primary" href="/contact">
                                    Contact Us
                                </Button>
                                <Button href="/privacy-policy">
                                    Privacy Policy
                                </Button>
                            </Space>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}

export default TermsOfService;
